import * as React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { analytics } from "../../analytics";
import { useOrganization } from "../../crud/organizations/hooks";
import {
  useCreateWorkspaceMutation,
  useWorkspaces,
} from "../../crud/workspaces/hooks";
import { Urls } from "../../domain/urls";
import { Page } from "../../components/layout/page";
import { UpgradeDialog } from "../../shared-components/UpgradeDialog";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";

const workspaceLimitAlertText = (
  <>
    Your organization has reached your plan&apos;s workspace limit.{" "}
    <Link component={RouterLink} to={`/${Urls.Pricing}`}>
      See plans
    </Link>{" "}
    to enable adding more workspaces.
  </>
);

export const CreateWorkspaceForm = ({
  organizationId,
}: {
  organizationId: number;
}): React.ReactElement => {
  const { data: org } = useOrganization(organizationId);
  const { data: organizationWorkspaces } = useWorkspaces({ organizationId });
  const [dialogOpen, setDialogOpen] = useState(false);

  const workspaceLimit = org?.workspaceLimit || null;
  const workspaceLimitReached =
    (organizationWorkspaces &&
      workspaceLimit &&
      organizationWorkspaces.length >= workspaceLimit) ||
    false;

  useEffect(() => {
    if (org && workspaceLimitReached) {
      analytics.track("create-workspace-blocked-by-limit");
      setDialogOpen(true);
    }
  }, [org, workspaceLimitReached]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<{
    name: string;
  }>({
    defaultValues: {
      name: "",
    },
  });
  const { mutate: createWorkspace, isLoading } = useCreateWorkspaceMutation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  return (
    <Page
      title="Create Workspace"
      breadCrumbs={[
        {
          text: "Team",
          link: `/${Urls.Team}`,
        },
        {
          text: org?.name || "",
        },
        {
          text: "Workspaces",
        },
        { text: "Create" },
      ]}
    >
      <UpgradeDialog
        organizationId={organizationId}
        title="Workspace Limit Reached"
        alertText={workspaceLimitAlertText}
        open={dialogOpen && workspaceLimitReached}
        onClose={() => setDialogOpen(false)}
      />
      <form
        onSubmit={handleSubmit((data) => {
          createWorkspace(
            {
              name: data.name,
              organizationId,
            },
            {
              onSuccess: () => {
                reset();
                enqueueSnackbar("Workspace created", {
                  variant: "success",
                });
                navigate(`/${Urls.Team}?showDefaultWorkspaceBanner=true`);
              },
              onError: async (err) => {
                const { message, code } = await err.response.json();
                if (code === "REQUIRE_UNIQUE_ACCOUNT_NAMES") {
                  setError("name", {
                    type: "validate",
                    message: "Workspace names must be globally unique",
                  });
                } else {
                  enqueueSnackbar(`Error: ${message}`, {
                    variant: "error",
                  });
                }
              },
            },
          );
        })}
      >
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography>
                Add a workspace to the organization:{" "}
                <strong>{org?.name}</strong>
              </Typography>
              <TextField
                {...register("name", {
                  minLength: {
                    value: 4,
                    message: "Must be at least 4 characters",
                  },
                  maxLength: {
                    value: 34,
                    message: "Must be less than 34 characters",
                  },
                  required: "Workspace name is required",
                })}
                placeholder="staging-workspace"
                disabled={workspaceLimitReached}
                helperText={
                  errors.name
                    ? errors.name.message
                    : "Workspace name, must be unique and between 4 and 34 characters."
                }
                size="small"
                error={!!errors.name}
                sx={{ width: "450px" }}
              />
            </Stack>
          </CardContent>
          <CardActions>
            <Stack direction={"row"} spacing={2}>
              <Button
                variant="secondary"
                component={RouterLink}
                to={`/${Urls.Team}`}
                onClick={() => reset()}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                loading={isLoading}
                disabled={workspaceLimitReached}
              >
                Create Workspace
              </LoadingButton>
            </Stack>
          </CardActions>
        </Card>
      </form>
    </Page>
  );
};
